import { CometChatAvatar, getLocalizedString } from '@cometchat/chat-uikit-react';
import '../../styles/CometChatDetails/CometChatUserDetails.css';
import React, { useContext } from 'react';
import { useCometChatContext } from '../../context/CometChatContext';
import { AppContext } from '../../context/AppContext';

interface UserDetailProps {
  user: CometChat.User;
  onHide?: () => void;
  actionItems?: {
    id?: string;
    name: string;
    icon: string;
  }[];
  showStatus?: boolean;
  onUserActionClick?: (item: { name: string; icon: string }) => void;
}

/**
 * Schema we expect on the user's CometChat metadata. Sourced from
 * Shopify customer fields and pushed into CometChat by App.tsx's
 * `refreshUserDetailsFromUrl`. All fields are optional — the renderer
 * skips any that are missing.
 */
interface ChatUserMetadata {
  memberNumber?: number | string;
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
  countryClubs?: Array<{
    name?: string;
    state?: string;
  }>;
  /**
   * Whether the member is open to receiving direct outreach from
   * other members. Sourced from Shopify customer metafield
   * `custom.open_for_outreach` (boolean). Undefined = not set,
   * which we treat as "no opinion expressed" and hide the row.
   */
  openForOutreach?: boolean;
}

/**
 * Badge image displayed next to the user's name when their
 * `openForOutreach` metadata flag is true. Sourced from the storefront
 * CDN so we don't have to ship a copy in this repo.
 */
const OUTREACH_BADGE_URL =
  "https://cdn.shopify.com/s/files/1/0677/2647/5492/files/redan-chequy-shadow.webp?v=1734644498";

function readMetadata(user: CometChat.User): ChatUserMetadata | null {
  try {
    const raw = user.getMetadata?.();
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as ChatUserMetadata;
    }
  } catch {
    /* SDK may throw if metadata isn't set */
  }
  return null;
}

function formatLocation(loc?: ChatUserMetadata['location']): string {
  if (!loc) return '';
  return [loc.city, loc.state, loc.country].filter(Boolean).join(', ');
}

export const CometChatUserDetails = (props: UserDetailProps) => {
  const { user, onHide = () => {}, actionItems = [], showStatus, onUserActionClick = () => {} } = props;
  const { chatFeatures } = useCometChatContext();
  const { appState } = useContext(AppContext);

  const metadata = readMetadata(user);
  const locationText = formatLocation(metadata?.location);
  const clubs = (metadata?.countryClubs ?? []).filter((c) => c?.name);
  const hasMember = metadata?.memberNumber != null && metadata.memberNumber !== '';
  // Only show the row when explicitly opted-in (true). Any other state
  // — `false`, missing, or null — hides the row entirely.
  const hasOutreach = metadata?.openForOutreach === true;
  const hasMetadataToShow =
    hasMember || !!locationText || clubs.length > 0 || hasOutreach;

  return (
    <>
      <div className="cometchat-user-details__header">
        <div className="cometchat-user-details__header-text">{getLocalizedString('user_info')}</div>
        <div className="cometchat-user-details__header-icon" onClick={onHide} />
      </div>
      <div className="cometchat-user-details__content">
        <div className="cometchat-user-details__content-avatar">
          <CometChatAvatar image={user.getAvatar?.()} name={user.getName()} />
        </div>
        <div>
          <div className="cometchat-user-details__content-title">{user.getName()}</div>
          {showStatus && chatFeatures.coreMessagingExperience.userAndFriendsPresence && (
            <div className="cometchat-user-details__content-description">
              {getLocalizedString('message_header_status_' + user.getStatus?.())}
            </div>
          )}
        </div>

        {hasMetadataToShow && (
          <div
            style={{
              width: '100%',
              padding: '12px 16px',
              borderTop: '1px solid var(--cometchat-border-color-light, #e8e8e8)',
              borderBottom: '1px solid var(--cometchat-border-color-light, #e8e8e8)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              boxSizing: 'border-box',
              textAlign: 'left',
            }}
          >
            {hasMember && (
              <MetadataRow
                label="Member"
                value={`#${metadata!.memberNumber}`}
              />
            )}
            {locationText && (
              <MetadataRow label="Location" value={locationText} />
            )}
            {clubs.length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--cometchat-text-color-primary, #141414)',
                    marginBottom: '6px',
                  }}
                >
                  Country Clubs
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {clubs.map((club, i) => (
                    <li
                      key={`${club.name}-${i}`}
                      style={{
                        fontSize: '16px',
                        color: 'var(--cometchat-text-color-primary, #141414)',
                        padding: '3px 0',
                      }}
                    >
                      {club.name}
                      {club.state ? ` · ${club.state}` : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {hasOutreach && (
              <div>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--cometchat-text-color-primary, #141414)',
                    marginBottom: '6px',
                  }}
                >
                  Open to Connect
                </div>
                <img
                  src={OUTREACH_BADGE_URL}
                  alt="Open to connect"
                  title="Open to connect"
                  style={{
                    height: '28px',
                    width: 'auto',
                    display: 'block',
                  }}
                />
              </div>
            )}
          </div>
        )}

        <div className="cometchat-user-details__content-action">
          {actionItems.map((actionItem) => (
            <div
              key={actionItem.name}
              className={`${appState.isFreshChat && actionItem.name === 'Delete Chat' ? 'cometchat-user-details__content-action-item-disabled' : ''} cometchat-user-details__content-action-item`}
              onClick={() =>
                appState.isFreshChat && actionItem.name === 'Delete Chat' ? () => {} : onUserActionClick(actionItem)
              }
            >
              <div
                className={`cometchat-user-details__content-action-item-icon cometchat-user-details__content-action-item-icon-${actionItem.id}`}
                style={
                  actionItem.icon ? { WebkitMask: `url(${actionItem.icon}), center, center, no-repeat` } : undefined
                }
              />
              <div className="cometchat-user-details__content-action-item-text">{actionItem.name}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

/**
 * Single labeled-value row used by the metadata block above. Layout is
 * a sentence-case label (bold) on top with the value on the line below,
 * both in the primary text color so the panel reads as a uniform list
 * rather than a sea of two-tone form fields.
 */
function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--cometchat-text-color-primary, #141414)',
          marginBottom: '2px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '16px',
          color: 'var(--cometchat-text-color-primary, #141414)',
        }}
      >
        {value}
      </div>
    </div>
  );
}
